class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.querySelector('html').classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.querySelector('html').classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
    this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (customElements && !customElements.get('protection-product')) {
  class ProtectionProduct extends HTMLElement {
    constructor() {
      super();
      this.STORAGE_KEY = 'protection_product_enabled';
      this.variantId = this.dataset.productId;
      this.price = this.dataset.productPrice;
      this.dataCartDrawer = null;

      this.checkbox = this.querySelector('.js-addon-toggle');
    }

    async connectedCallback() {
      this.initializeCheckboxState();
      this.attachEventListeners();

      await this.initDataCartDrawer();
    }

    async initDataCartDrawer() {
      const dataElement = document.querySelector('[id="data-cart-drawer"]');

      if (dataElement) {
        this.dataCartDrawer = JSON.parse(dataElement.innerHTML);
        return this.dataCartDrawer;
      }

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart.js');

        if (!response.ok) throw new Error(`Could not fetch cart, status: ${response.status}`);

        const cartData = await response.json();

        this.dataCartDrawer = cartData;
        return cartData;

      } catch (err) {
        console.log(err);
        return null;
      }
    }

    async addProductToCart() {
      let updates = {
        [this.variantId]: 1
      };

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ updates })
        });

        if (!response.ok) throw new Error(`Could not fetch ${url}, status: ${response.status}`);

        const data = await response.json();

        return data;
      } catch (err) {
        console.log(err);
      }
    }

    async removeProductFromCart() {
      try {
        let updates = {
          [this.variantId]: 0
        };

        const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ updates })
        });

        if (!response.ok) throw new Error(`Could not fetch ${url}, status: ${response.status}`);

        const data = await response.json();

        return data;

      } catch (error) {
        console.error('Error:', error);
      }
    }

    async updateDrawerHTML() {
      try {
        const response = await fetch(window.Shopify.routes.root + '?section_id=cart-drawer').then(res => res.text());
        const html = new DOMParser().parseFromString(response, 'text/html');

        const targetElement = document.querySelector('cart-drawer .js-drawer-inner');
        const sourceElement = html.querySelector('cart-drawer .js-drawer-inner');

        if (!targetElement || !sourceElement) return;

        targetElement.replaceWith(sourceElement);
      } catch (err) {
        console.log(err);
      }
    }

    async handleCheckboxChange(event) {
      event.stopPropagation()

      const isChecked = event.target.checked;
      this.saveStorageState(isChecked);

      if (isChecked) {
        await this.addProductToCart();
      } else {
        await this.removeProductFromCart();
      }

      await this.updateDrawerHTML();
    }

    initializeCheckboxState() {
      const existingState = localStorage.getItem(this.STORAGE_KEY);

      if (existingState === null) {
        this.saveStorageState(false);
      }

      if (this.checkbox) {
        this.checkbox.checked = this.getStorageState();
      }
    }

    attachEventListeners() {
      if (this.checkbox) {
        this.checkbox.addEventListener('change', this.handleCheckboxChange.bind(this)
        );
      }
    }

    getStorageState() {
      const state = localStorage.getItem(this.STORAGE_KEY);
      return state === 'true';
    }

    saveStorageState(value) {
      localStorage.setItem(this.STORAGE_KEY, value);
      return value;
    }
  }

  customElements.define('protection-product', ProtectionProduct);
}